import React, { useCallback, useEffect, useState } from "react";
import { changeUserBalance, deleteUser, User } from "../../ApiService";
import styles from "./ManageTable.module.scss";
import TextField from "../../components/TextField/TextField";
import Button from "../../components/Button/Button";
import LoaderCircle from "../../components/LoaderCircle/LoaderCircle";
import { useMutation, useQueryClient } from "react-query";
import Modal from "../../components/Modal/Modal";
import IconButton from "../../components/IconButton/IconButton";

export type ManageTableProps = {
  userList: User[];
};

const userTypes = {
  admin: "Админ",
  user: "Юзер",
};

const ManageTable: React.FC<ManageTableProps> = ({ userList }) => {
  const [users, setUsers] = useState<User[]>(userList);
  const [isLoading, setIsLoading] = useState(false);
  const [userDelete, setUserDelete] = useState<string>("");

  const queryClient = useQueryClient();

  const updateBalanceMutation = useMutation(changeUserBalance, {
    onMutate: async () => {
      setIsLoading(true);
      await queryClient.cancelQueries(["userList"]);
      const previousUsers = queryClient.getQueryData(["userList"]);
      return { previousUsers };
    },
    onError: (err, variables, context) => {
      console.log(err, variables);
      queryClient.setQueryData(["userList"], context?.previousUsers);
      setIsLoading(false);
    },
    onSuccess: (updatedUser) => {
      queryClient.setQueryData(["userList"], (oldUsers: any) =>
        oldUsers.map((user: User) =>
          user.id === updatedUser.id ? updatedUser : user
        )
      );
      const currentUser = queryClient.getQueryData<User>("userInfo");
      if (updatedUser.id === currentUser?.id) {
        queryClient.refetchQueries("userInfo");
      }
      setIsLoading(false);
    },
  });

  const deleteUserMutation = useMutation(deleteUser, {
    onMutate: async () => {
      setIsLoading(true);
      await queryClient.cancelQueries(["userList"]);
      const previousUsers = queryClient.getQueryData(["userList"]);
      return { previousUsers };
    },
    onError: (err, variables, context) => {
      console.log(err, variables);
      queryClient.setQueryData(["userList"], context?.previousUsers);
      setIsLoading(false);
    },
    onSuccess: (result) => {
      setIsLoading(false);
      queryClient.setQueryData(
        ["userList"],
        (oldUsers: User[] | undefined) =>
          oldUsers?.filter((user: User) => user.id !== result.id) ?? []
      );
    },
  });

  const handleChangeBalance = (index: number, balance: number) => {
    const updatedUsers = [...users];
    updatedUsers[index] = { ...updatedUsers[index], tokenBalance: balance };
    setUsers(updatedUsers);
  };

  const handleSaveBalance = (index: number) => {
    const userBalance = {
      id: users[index].id,
      tokenBalance: users[index].tokenBalance,
    };

    updateBalanceMutation.mutate(userBalance);
  };

  const handleDeleteUser = (id: string) => {
    deleteUserMutation.mutate(id);
    setUserDelete("");
  };

  useEffect(() => {
    setUsers(userList);
  }, [userList]);

  return (
    <div>
      {isLoading && <LoaderCircle />}
      <table className={styles.table}>
        <thead>
          <tr>
            <th>
              <div className={styles.thDiv}>Имя</div>
            </th>
            <th>
              <div className={styles.thDiv}>Email</div>
            </th>
            <th>
              <div className={styles.thDiv}>Баланс токенов</div>
            </th>
            <th>
              <div className={styles.thDiv}>Роль</div>
            </th>
            <th>
              <div
                className={styles.thDiv}
                style={{ border: "none", paddingRight: 16 }}
              >
                Удаление
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {users.map((user, index) => {
            const saveBalance = () => handleSaveBalance(index);
            const deleteUser = () => setUserDelete(user.id);
            return (
              <tr key={index}>
                <td>{user.name}</td>
                <td>{user.email}</td>
                <td className="flex">
                  <div className="flex gap-2 items-center">
                    <TextField
                      type="number"
                      placeholder="Токены"
                      onChange={(event) =>
                        handleChangeBalance(index, Number(event.target.value))
                      }
                      value={user.tokenBalance}
                    />
                    <IconButton
                      onClick={saveBalance}
                      active={
                        userList[index]?.tokenBalance !== user.tokenBalance
                      }
                      icon={"check-circle"}
                    />
                  </div>
                </td>
                <td>{userTypes[user.type]}</td>

                <td>
                  <IconButton
                    onClick={deleteUser}
                    icon="trash"
                    animation={"rotation"}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <Modal isOpen={!!userDelete} onClose={() => setUserDelete("")}>
        <p className={styles.h3Font}>
          {`Вы действительно хотите удалить пользователя ${
            users.find((user) => user.id === userDelete)?.name
          }?`}
        </p>
        <div className="flex justify-around mt-4">
          <Button
            title="Удалить"
            status="alert"
            onClick={() => handleDeleteUser(userDelete)}
          />
          <Button title="Отмена" onClick={() => setUserDelete("")} />
        </div>
      </Modal>
    </div>
  );
};

export default ManageTable;
